import { patchState, signalStore, withComputed, withMethods, withProps, withState } from "@ngrx/signals";
import { s25Resource } from "../../../util/s25.resource";
import { UserprefService } from "../../../services/userpref.service";
import { EventMicroService } from "../../../services/event.micro.service";
import { S25Util } from "../../../util/s25-util";
import { computed, inject } from "@angular/core";
import { Space, WsDataI, WSReservations } from "../../s25-event/EventMicroI";
import { StudentEngagementUtil } from "../student.engagement.util";
import { OrganizationMicroService } from "../../../services/organization.micro.service";
import { CustomAttributes } from "../../../pojo/CustomAttributes";
import { attendanceDisplayName, attendanceState, AttendanceState } from "../attendance/attendance.service";
import { SeOrgSecurityStore } from "../se.org.security.store";
import { SeAttendanceStore } from "../attendance/se.attendance.store";
import { StudentEngagementService } from "../student.engagement.service";
import { S25Datefilter } from "../../s25-dateformat/s25.datefilter.service";
import { ModalService } from "../../modal/modal.service";
import { ContactService } from "../../../services/contact.service";
import { Attendance } from "../attendance/se.attendance.util";
import { Event } from "../../../pojo/Event";
import { SeSecurityStore } from "../se.security.store";
import { Category } from "../../../pojo/Category";
import { SeSearchService } from "../search/se.search.service";

interface SEEventDetailsState {
    eventId: number | undefined;
    showRegistrationForm: boolean;
    portalAccessDenied: boolean;
}

export const SEEventDetailsStore = signalStore(
    withState<SEEventDetailsState>({
        eventId: undefined,
        showRegistrationForm: false,
        portalAccessDenied: false,
    }),
    // Internals
    withMethods((store) => ({
        _formatSpaceName: (space: Space) =>
            `${space.spaceName}${space.spaceFormalName ? ` - ${space.spaceFormalName}` : ""}`,
    })),
    withProps((store) => ({
        _orgSecStore: inject(SeOrgSecurityStore),
        _attendanceStore: inject(SeAttendanceStore),
        _seService: inject(StudentEngagementService),
        _securityStore: inject(SeSecurityStore),
    })),
    withProps((store) => ({
        _dateFormat: s25Resource({ loader: () => UserprefService.getS25Dateformat() }),
        _timeFormat: s25Resource({ loader: () => UserprefService.getS25Timeformat() }),
        _dateTimeFormat: s25Resource({ loader: () => UserprefService.getS25DateTimeformat() }),
        _rawEvent: s25Resource<WsDataI | undefined, number | undefined>({
            params: () => store.eventId(),
            loader: async ({ params: eventId, abortSignal }) => {
                patchState(store, { portalAccessDenied: false });

                if (!store._securityStore.hasFLSOverride()) {
                    const allowed = await SeSearchService.isConnect25EventVisibleInPortalTile(eventId);
                    if (abortSignal.aborted) return undefined;
                    if (!allowed) {
                        patchState(store, { portalAccessDenied: true });
                        return undefined;
                    }
                }

                const [event] = await S25Util.Maybe(EventMicroService.getEventDetailById([eventId], true, ["all"]));
                if (abortSignal.aborted) return undefined;
                if (!event) return undefined;
                return event.content;
            },
        }),
    })),
    withComputed((store) => ({
        _eventDetails: () => store._rawEvent.value()?.data?.items?.[0],
        _expandedDetails: () => store._rawEvent.value()?.expandedInfo ?? {},
        _dateFormats: () => {
            if (!store._dateFormat.value() || !store._timeFormat.value() || !store._dateTimeFormat.value())
                return undefined;
            return {
                date: store._dateFormat.value()!,
                time: store._timeFormat.value()!,
                dateTime: store._dateTimeFormat.value()!,
            };
        },
    })),
    withComputed((store) => ({
        _hostOrgId: () =>
            S25Util.array.forceArray(store._eventDetails()?.organizations).find((org) => !!org!.primary)?.primary,
    })),
    withProps((store) => ({
        _hostOrg: s25Resource({
            params: () => store._hostOrgId(),
            loader: async ({ params: hostOrgId, abortSignal }) => {
                const [org] = await S25Util.Maybe(
                    OrganizationMicroService.getOrganizationIncludes([hostOrgId], ["attributes"], false),
                );
                return org?.data?.items?.[0];
            },
        }),
    })),
    // Public computes
    withComputed((store) => {
        const occurrences = computed(() => {
            const eventDetails = store._eventDetails();
            const dateFormats = store._dateFormats();
            if (!eventDetails || !dateFormats) return undefined;

            const spaceMap = new Map(
                store._expandedDetails()?.spaces?.map((space) => [space.spaceId, store._formatSpaceName(space)]),
            );
            const userAttendanceMap = new Map(
                store._attendanceStore.userAttendance()?.attendance?.map((att) => [att.reservationId, att]),
            );

            return eventDetails.profiles
                ?.flatMap((profile) => {
                    profile.reservations?.sort(
                        (a: WSReservations, b: WSReservations) => +new Date(a.rsrvStartDt!) - +new Date(b.rsrvStartDt!),
                    );
                    return profile.reservations
                        ?.filter((res: WSReservations) => res.state !== Event.Reservation.States.Cancelled)
                        .map((res: WSReservations) => {
                            return {
                                reservationId: res.rsrvId!,
                                evStartDt: <string>res.evStartDt,
                                evEndDt: <string>res.evEndDt,
                                startDate: S25Datefilter.transform(res.evStartDt, dateFormats.dateTime),
                                date: StudentEngagementUtil.formatDate(
                                    <string>res.evStartDt,
                                    <string>res.evEndDt,
                                    dateFormats,
                                ),
                                hasEnded: S25Util.date.isAfter(<string>res.evEndDt),
                                locations:
                                    res.spaces
                                        ?.flatMap((space) =>
                                            space.reserved?.map((space: Space) => spaceMap.get(space.spaceId) ?? ""),
                                        )
                                        .filter(S25Util.isDefined) ?? [],
                                userAttendance: userAttendanceMap.get(res.rsrvId!),
                                notes: res.comments,
                            };
                        });
                })
                ?.filter(S25Util.isDefined);
        });

        return {
            loaded: () => !!store._eventDetails(),
            loading: () => !!store._rawEvent.resource.isLoading(),
            name: () => store._eventDetails()?.name,
            title: () => store._eventDetails()?.title,
            date: () => {
                const occs = occurrences();
                const dateFormats = store._dateFormats();
                if (!occs?.length || !dateFormats) return undefined;

                const minStart = occs[0].evStartDt;
                const maxEnd = occs[occs.length - 1].evEndDt;
                return StudentEngagementUtil.formatDate(minStart, maxEnd, dateFormats);
            },
            locations: () => {
                const spaces = <number[]>store
                    ._eventDetails()
                    ?.profiles?.map((p) =>
                        p.reservations?.map((r: WSReservations) =>
                            r.spaces?.map((s) => s.reserved?.map((s) => s.spaceId)),
                        ),
                    )
                    .flat(3)
                    .filter(S25Util.isDefined);
                const spaceMap = new Map(
                    store._expandedDetails()?.spaces?.map((space) => {
                        return [space.spaceId, { id: space.spaceId, name: store._formatSpaceName(space) }];
                    }) ?? [],
                );
                return S25Util.array
                    .unique(spaces)
                    .map((id) => spaceMap.get(id))
                    .filter(S25Util.isDefined);
            },
            resources: () => {
                return store
                    ._expandedDetails()
                    ?.resources?.map((res) => ({ id: res.resourceId, name: res.resourceName }));
            },
            categories: () => {
                const categories = store._eventDetails()?.categories?.map((cat) => cat.categoryId) ?? [];
                const filtered = categories.filter(S25Util.isDefined);
                const names = new Map(
                    store._expandedDetails()?.categories?.map((cat) => [cat.categoryId, cat.categoryName]),
                );
                return filtered.map((id) => ({
                    id,
                    name: names.get(id) ?? "Unknown Category",
                }));
            },
            hostOrg: () => {
                const org = store._hostOrg.value();
                if (!org) return undefined;
                const logoAttributeId = CustomAttributes.system.StudentEngagementOrgLogo.id;
                const logoAttribute = org.attributes?.find((attr) => attr.attributeId === logoAttributeId);
                return {
                    itemId: org.id,
                    itemName: org.organizationName,
                    logoUrl: StudentEngagementUtil.parseUrl(logoAttribute?.value) ?? undefined,
                };
            },
            additionalOrgs: () => {
                const orgs = store._eventDetails()?.organizations?.ancillary;
                const orgMeta = store._expandedDetails().organizations;
                const orgNames = new Map(orgMeta?.map((org) => [org.organizationId, org.organizationName]));
                if (!orgs || !orgMeta) return undefined;
                return orgs
                    .filter((org) => !org.primary)
                    .map((org) => ({
                        id: org.organizationId,
                        name: orgNames.get(org.organizationId) ?? "Unknown Organization",
                    }));
            },
            imageAttr: () => {
                return S25Util.array
                    .forceArray(store._eventDetails()?.attributes)
                    ?.find((attr) => attr!.attributeId === CustomAttributes.system.HotEventImage.id);
            },
            occurrences,
            followState: () => {
                const attendance = store._attendanceStore.userAttendance()?.attendance ?? [];
                const reservations = store._eventDetails()?.profiles?.flatMap((p) => p.reservations) ?? [];
                if (!attendance.length) return "NOT_FOLLOWING";
                const allFollowed = attendance.length && attendance.length === reservations.length;
                if (!allFollowed) return "PARTIAL";
                const onlyFollowed = attendance.every((att) => att.attendeeState === attendanceState.INTERESTED);
                if (!onlyFollowed) return "MIXED";
                return "FOLLOWING";
            },
            attendanceState: () => {
                const attendance = store._attendanceStore.userAttendance()?.attendance ?? [];
                const reservations = store._eventDetails()?.profiles?.flatMap((p) => p.reservations!) ?? [];

                const registered = attendance.filter((att) =>
                    Attendance.State.isAtLeast(att.attendeeState, attendanceState.REGISTERED),
                );
                const registeredIds = new Set(registered.map((att) => att.reservationId));
                const unregistered = reservations.filter((res: WSReservations) => !registeredIds.has(res.rsrvId!));

                const states = new Set(attendance.map((att) => att.attendeeState));
                return {
                    mixed: states.size > 1 && !!registered.length,
                    state: attendanceDisplayName[attendance[0]?.attendeeState ?? 0],
                    registered,
                    unregistered,
                };
            },
            registeredOccurrences: () => {
                const ids =
                    store._attendanceStore
                        .userAttendance()
                        ?.attendance?.filter((att) => att.attendeeState !== attendanceState.INTERESTED)
                        .map((att) => att.reservationId) ?? [];
                return new Set(ids);
            },
            canUnregister: () => {
                return occurrences()?.filter((occ) => {
                    if (occ.hasEnded) return false;
                    const state = occ.userAttendance?.attendeeState;
                    return state === attendanceState.PENDING || state === attendanceState.REGISTERED;
                });
            },
            startDate: () => {
                const start = store._eventDetails()?.dates?.startDate;
                return start ? S25Util.date.parseDropTZ(start) : undefined;
            },
            endDate: () => {
                const end = store._eventDetails()?.dates?.endDate;
                return end ? S25Util.date.parseDropTZ(end) : undefined;
            },
            locator: () => store._eventDetails()?.eventLocator,
            requirements: () => {
                const items = store._eventDetails()?.requirements;
                const meta = store._expandedDetails().requirements;
                const names = new Map(meta?.map((org) => [org.requirementId, org.requirementName]));
                if (!items || !meta) return undefined;
                return items.map((item) => ({
                    id: item.requirementId,
                    name: names.get(item.requirementId) ?? "Unknown Requirement",
                    quantity: item.quantity,
                    comments: item.comments,
                }));
            },
            confirmationText: () => store._eventDetails()?.text?.find((text) => text.confirmation)?.confirmation,
            description: () => store._eventDetails()?.text?.find((text) => text.description)?.description,
            internalNotes: () => store._eventDetails()?.text?.find((text) => text.note)?.note,
            comments: () =>
                store
                    ._eventDetails()
                    ?.profiles?.flatMap((p) => p.comments)
                    .filter(S25Util.isDefined),
            attendanceCounts: () => {
                const attendanceCounts: Record<AttendanceState, number> = S25Util.fromEntries(
                    Array.from(S25Util.values(attendanceState)).map((state) => [state, 0]),
                );
                const attendees = store._attendanceStore.attendees();
                for (const attendee of attendees ?? []) {
                    for (const reservation of attendee.reservations ?? []) {
                        attendanceCounts[reservation.attendeeState]++;
                    }
                }
                return attendanceCounts;
            },
            eventType: () => {
                const id = store._eventDetails()?.context?.typeId;
                const meta = store._expandedDetails()?.eventTypes ?? [];
                const type = meta.find((type) => type.typeId === id);
                if (!id || !type) return;
                return { id, name: type.typeName };
            },
            eventState: () => {
                const id = store._eventDetails()?.context?.state;
                const meta = store._expandedDetails()?.states ?? [];
                const state = meta?.find((state) => state.state === id);
                if (!S25Util.isDefined(id) || !state) return;
                return { id, name: state.stateName };
            },
            attributes: () => {
                const items = store._eventDetails()?.attributes;
                const meta = store._expandedDetails().attributes;
                const expanded = new Map(meta?.map((item) => [item.attributeId, item]));
                if (!items || !meta || !expanded) return undefined;

                return items.map((item) => {
                    const expandedItem = expanded.get(item.attributeId);
                    const isMulti =
                        expandedItem?.dataType === CustomAttributes.type.Text &&
                        typeof item.value === "string" &&
                        S25Util.array.isArray(S25Util.parseJson(item.value)[0]);
                    const isNumericalType = /^\d+$/.test(String(expandedItem?.dataType));
                    const multiVal = isMulti
                        ? (<{ item: string }[]>S25Util.parseJson(item.value)[0]!).map(({ item }) => ({
                              itemName: item,
                          }))
                        : [];
                    return {
                        custAttrId: item.attributeId,
                        itemId: isNumericalType ? Number(item.value) : item.attributeId,
                        itemName: isMulti ? multiVal : isNumericalType ? undefined : item.value,
                        itemLabel: expandedItem?.attributeName ?? "Unknown Attribute",
                        itemTypeId: isNumericalType ? Number(expandedItem!.dataType) : expandedItem?.dataType,
                        multi_val: isMulti,
                    };
                });
            },
            contactRoles: () => {
                const roles = store._eventDetails()?.roles;
                const contactMeta = store._expandedDetails()?.contacts;
                const roleMeta = store._expandedDetails()?.roles;
                if (!roles || !contactMeta || !roleMeta) return;
                const contactMap = new Map(
                    contactMeta.map((contact) => [
                        contact.contactId,
                        { ...contact, fullName: ContactService.getFullName(contact.firstName, contact.familyName) },
                    ]),
                );
                const roleNames = new Map(roleMeta.map((role) => [role.roleId, role.roleName]));
                return roles.map((role) => ({
                    id: role.roleId,
                    name: roleNames.get(role.roleId!),
                    contact: contactMap.get(role.contactId!)!,
                }));
            },
        };
    }),
    // Public methods
    withMethods((store) => ({
        setEventId: (eventId: number) => {
            patchState(store, { eventId });
            store._attendanceStore.setEventId(eventId);
        },
        registerForOccurrences: async () => {
            const occurrences = store.occurrences();
            if (!occurrences?.length) return;
            let occIds = occurrences.map((occ) => occ.reservationId);
            if (occIds.length > 1) {
                const occs = occurrences
                    .filter(
                        (occ) =>
                            !occ.userAttendance ||
                            Attendance.State.isAtMost(occ.userAttendance.attendeeState, attendanceState.INTERESTED),
                    )
                    .map((occ) => ({ id: occ.reservationId, label: occ.date, included: false }));
                const ok = await ModalService.modal("se-register-occurrences", { occs });
                if (!ok) return;
                occIds = occs.filter((occ) => occ.included).map((occ) => occ.id);
            }
            await store._attendanceStore.setAttendance("REGISTERED", { occurrences: occIds });
        },
        unregisterForOccurrences: async () => {
            const occurrences = store.canUnregister();
            if (!occurrences?.length) return;
            const occs = occurrences.map((occ) => ({ id: occ.reservationId, label: occ.date, included: false }));
            const ok = await ModalService.modal("se-register-occurrences", {
                title: "Which occurrences do you wish to unregister from?",
                occs,
            });
            if (!ok) return;
            const occIds = occs.filter((occ) => occ.included).map((occ) => occ.id);
            await store._attendanceStore.setAttendance("INTERESTED", { occurrences: occIds });
        },
        setShowRegistrationForm: (show: boolean) => patchState(store, { showRegistrationForm: show }),
    })),
);

function connect25EventCategoryIds(eventItem: { categories?: { categoryId?: number }[] } | undefined): number[] {
    return eventItem?.categories?.map((category) => category.categoryId).filter(S25Util.isDefined) ?? [];
}

function isConnect25EventUniverse(categoryIds: number[]): boolean {
    return categoryIds.includes(Category.SystemDefined.StudentEngagement);
}
